import { mapKeys } from "es-toolkit";

import { insertedNodeType } from "@/components/suggestions/utils/libraryItem";
import { hasOneCodepoint } from "@/utils/codePointLength";
import { isInputCaseSensitive } from "@/utils/isInputCaseSensitive";

import { suggestionToNodeJson } from "./suggestionToNodeJson";

import type { SingleSuggestion, SuggestionNodeType } from "@/components/suggestions/types";
import type { NodeLookupModel } from "@/models/NodeLookupModel";
import type { LocaleKeywords, KeywordLang, InputMode } from "@corca/data";

const PRIORITY_FACTORS = {
  EXACT_NAME_MATCH: 0.8,
  SYMBOL_EXACT_NAME_MATCH: 0.2,
  NAME_MATCH: 0.5,
  CASE_SENSITIVE_MATCH: 0.2,
  EXISTING_FUNCTION: 0.6,
  CODE_MATCH: 0.5,
  EXACT_CODE_MATCH: 2,
  EXACT_KEYWORD_MATCH: 0.2,
};

export function strEqual(str: string | undefined, input: string) {
  if (!str || !input) return false;
  // normalize special unicode characters, for example: "𝑥" -> "x"
  return str === input;
}

export function startsWith(str: string | undefined, input: string) {
  if (!str || !input) return false;
  return str.startsWith(input);
}

type MatchOptions<T extends SuggestionNodeType> = {
  suggestion: SingleSuggestion<T>;
  input: string;
  lcInput: string;
  caseSensitive: boolean;
  isSingle: boolean;
  isLetters: boolean;
};

function matchInputToValue<T extends SuggestionNodeType>(
  value: string | undefined,
  compare: (str: string | undefined, inp: string) => boolean,
  priorityAddition: number,
  matchOptions: MatchOptions<T>,
) {
  if (!value) return null;
  const { suggestion, input, lcInput } = matchOptions;

  // we don't want to normalize symbols like "…" into "..." here, as it is messing up symbol matching
  // see also: https://linear.app/corca-research/issue/D-2636/suggestions-dont-match-by-symbol-if-keywords-are-present
  const normalizedValue = matchOptions.isLetters ? value.normalize("NFKC").toLowerCase() : value;
  const match = compare(normalizedValue, lcInput);

  if (match) {
    const csMatch = compare(value, input);

    let priority = suggestion.priority + priorityAddition;

    if (csMatch && matchOptions.isLetters) {
      priority += PRIORITY_FACTORS.CASE_SENSITIVE_MATCH;
    }

    return { matchedString: value, priority };
  }

  return null;
}

type KeywordLangCache = Map<
  SingleSuggestion["item"],
  [Record<string, number>, Record<string, number>]
>;
const keywordCache = new Map<KeywordLang, KeywordLangCache>();

function cachedLangKeywords(lang: KeywordLang, item: SingleSuggestion["item"]) {
  let langCache: KeywordLangCache;
  if (!keywordCache.has(lang)) {
    langCache = new Map();
    keywordCache.set(lang, langCache);
  } else {
    langCache = keywordCache.get(lang)!;
  }

  if (langCache.has(item)) {
    return langCache.get(item)!;
  }

  const localeKeywords: LocaleKeywords | undefined = item.variantKeywords ?? item.keywords;

  const keywords =
    (lang === "en" ? localeKeywords?.en : { ...localeKeywords?.en, ...localeKeywords?.[lang] }) ??
    {};
  const lcKeywords = mapKeys(keywords, (v, k) => k.toLowerCase());

  langCache.set(item, [keywords, lcKeywords]);

  return [keywords, lcKeywords];
}

export function matchesKeyword(
  item: SingleSuggestion["item"],
  str: string,
  lang: KeywordLang,
): boolean {
  const [, lcKeywords] = cachedLangKeywords(lang, item);
  return lcKeywords[str.toLowerCase()] > 0;
}

// TODO: use only keywords for matching
// keywords should be auto-created from name, symbol, and code with corresponding priorities

function matchAndPrioritizeSuggestion<T extends SuggestionNodeType>(
  matchOptions: MatchOptions<T>,
  nodeLookup?: NodeLookupModel | null,
  lang: KeywordLang = "en",
  skipPrioritize?: boolean,
): SingleSuggestion<T> | null {
  const { suggestion, input, lcInput, isSingle } = matchOptions;

  let onlyKeywordsMatch = false;

  const matchedPriorities: ({ priority: number; matchedString: string } | null)[] = [];

  // see also modifyInput() in providers/utils.ts
  const symbol = suggestion.item.symbol === "ƒ" ? "f" : suggestion.item.symbol;
  let { displayName } = suggestion.item;
  const { code } = suggestion.item;

  if (displayName && suggestion.item.isSymbol) {
    displayName = displayName.normalize("NFKC").toLowerCase();
  }

  if (!hasOneCodepoint(symbol)) {
    matchedPriorities.push(
      matchInputToValue(symbol, strEqual, PRIORITY_FACTORS.EXACT_NAME_MATCH, matchOptions),
    );
  } else {
    matchedPriorities.push(
      matchInputToValue(symbol, strEqual, PRIORITY_FACTORS.SYMBOL_EXACT_NAME_MATCH, matchOptions),
    );
  }

  if (!suggestion.item.isSymbol) {
    matchedPriorities.push(
      matchInputToValue(displayName, strEqual, PRIORITY_FACTORS.EXACT_NAME_MATCH, matchOptions),
    );
  }

  if (input.length > 0 && !isSingle) {
    matchedPriorities.push(
      matchInputToValue(symbol, startsWith, PRIORITY_FACTORS.NAME_MATCH, matchOptions),
    );

    if (!suggestion.item.isSymbol) {
      matchedPriorities.push(
        matchInputToValue(displayName, startsWith, PRIORITY_FACTORS.NAME_MATCH, matchOptions),
      );
    }

    if (code) {
      matchedPriorities.push(
        matchInputToValue(code, startsWith, PRIORITY_FACTORS.CODE_MATCH, matchOptions),
      );

      matchedPriorities.push(
        matchInputToValue(code, strEqual, PRIORITY_FACTORS.EXACT_CODE_MATCH, matchOptions),
      );
    }
  }

  const [keywords, lcKeywords] = cachedLangKeywords(lang, suggestion.item);

  // adjust keyword priorities to match the change of suggestion priority
  const priorityIncrement = suggestion.item.isSymbol ? 0 : Math.max(-1, suggestion.priority - 1);

  if (isSingle) {
    const keywordMatch = input in keywords;
    const lcKeywordMatch = lcInput in lcKeywords;

    if (lcKeywordMatch) {
      onlyKeywordsMatch = matchedPriorities.filter(Boolean).length === 0;

      let priority = keywords[input] ?? lcKeywords[lcInput] ?? suggestion.priority;

      priority += priorityIncrement;

      if (keywordMatch && matchOptions.isLetters) {
        priority += PRIORITY_FACTORS.CASE_SENSITIVE_MATCH;
      }

      matchedPriorities.push({ matchedString: keywordMatch ? input : lcInput, priority });
    }
  } else {
    const keywordMatch = Object.hasOwn(keywords, input)
      ? input
      : Object.keys(keywords).find((keyword) => keyword.startsWith(input));

    const lcKeywordMatch = Object.hasOwn(lcKeywords, lcInput)
      ? lcInput
      : Object.keys(lcKeywords).find((keyword) => keyword.startsWith(lcInput));

    if (lcKeywordMatch) {
      onlyKeywordsMatch = matchedPriorities.filter(Boolean).length === 0;

      let priority =
        keywords[keywordMatch ?? ""] ?? lcKeywords[lcKeywordMatch] ?? suggestion.priority;

      priority += priorityIncrement;

      if (keywordMatch && matchOptions.isLetters) {
        priority += PRIORITY_FACTORS.CASE_SENSITIVE_MATCH;
      }

      if (lcKeywordMatch === lcInput) {
        priority += PRIORITY_FACTORS.EXACT_KEYWORD_MATCH;
      }

      matchedPriorities.push({ matchedString: keywordMatch ?? lcKeywordMatch, priority });
    }
  }

  const filteredPriorities = matchedPriorities.filter((p) => p !== null);

  if (filteredPriorities.length === 0) return null;

  let topPriority = filteredPriorities.reduce<{ matchedString: string; priority: number } | null>(
    (max, p) => {
      if (!max) return p;
      return p && p.priority > max.priority ? p : max;
    },
    null,
  );

  if (!topPriority) return null;

  if (!onlyKeywordsMatch && topPriority.priority < suggestion.priority) {
    topPriority = { ...topPriority, priority: suggestion.priority };
  }

  if (insertedNodeType(suggestion.item) === "function") {
    // TODO: this could be cached
    const json = suggestionToNodeJson(suggestion);
    if (json && nodeLookup?.has(json, { types: ["function"], uniqBy: "params" })) {
      topPriority.priority += PRIORITY_FACTORS.EXISTING_FUNCTION;
    }
  }

  if (skipPrioritize) return { ...suggestion, matchedString: topPriority.matchedString };

  return { ...suggestion, ...topPriority };
}

/**
 * Prioritizes suggestions based on the input.
 */
export function prioritizeSuggestions<T extends SuggestionNodeType>(
  input: string,
  suggestions: SingleSuggestion<T>[],
  nodeLookup?: NodeLookupModel | null,
  lang: KeywordLang = "en",
): SingleSuggestion<T>[] {
  if (!input) return suggestions;

  const lcInput = input.normalize("NFKC").toLowerCase();
  const caseSensitive = isInputCaseSensitive(input);
  const isSingle = hasOneCodepoint(input);
  const isLetters = /^\p{L}*$/u.test(lcInput);

  return suggestions.map((suggestion) => {
    const matchOptions = {
      suggestion,
      input,
      lcInput,
      caseSensitive,
      isSingle,
      isLetters,
    };

    return matchAndPrioritizeSuggestion(matchOptions, nodeLookup, lang) ?? suggestion;
  });
}

type FilterSuggestionsOptions<T extends SuggestionNodeType> = {
  filterFn?: (item: SingleSuggestion<T>) => boolean;
  nodeLookup?: NodeLookupModel | null;
  lang?: KeywordLang;
  skipPrioritize?: boolean;
  inputMode?: InputMode;
};

/**
 * Prioritizes and filters out suggestions that don't match the input.
 */
export function filterSuggestions<T extends SuggestionNodeType>(
  input: string,
  suggestions: SingleSuggestion<T>[],
  options?: FilterSuggestionsOptions<T>,
) {
  if (!input) {
    return [];
  }

  const {
    filterFn = () => true,
    nodeLookup,
    lang = "en",
    skipPrioritize,
    inputMode,
  } = options ?? {};

  const lcInput = input.normalize("NFKC").toLowerCase();
  const caseSensitive = isInputCaseSensitive(input);
  const isSingle = hasOneCodepoint(input);
  const isLetters = /^\p{L}*$/u.test(lcInput);

  return suggestions
    .map((suggestion) => {
      if (!filterFn(suggestion)) return null;

      if (suggestion.inputMode && suggestion.inputMode !== inputMode) {
        return null;
      }

      const matchOptions = {
        suggestion,
        input,
        lcInput,
        caseSensitive,
        isSingle,
        isLetters,
      };

      return matchAndPrioritizeSuggestion(matchOptions, nodeLookup, lang, skipPrioritize);
    })
    .filter(Boolean);
}
